// The props-only canvas view for /transcripts/$id (docs/09 §4, UI-LAW §4c
// Workspace): audio strip first, one identity row, then a SplitPane —
// conversation left, results rail right — with independent scroll. Traces,
// Variables and Notes open in a Drawer (`?panel=`); Add a question renders
// the marker-first form inside the rail (`?label=new`), never in a layer, so
// an utterance range picked in the conversation becomes the Label's
// evidence. The container keeps every query mounted at route level.

import { useMemo, type ReactNode } from "react";

import type { components } from "../api/schema";
import type { TranscriptTimelinePanel } from "../lib/routerSearch";
import { isShowcaseMode } from "../lib/showcaseMode";
import { transcriptStatusTone } from "../lib/transcriptStatusTone";
import type { ListQueryResult } from "../lib/useListQuery";
import {
  type AudioPlayerSeekRequest,
  Badge,
  Button,
  Drawer,
  Inline,
  InlineError,
  Page,
  PageHeader,
  Skeleton,
  SplitPane,
  Text,
} from "../ui";
import { TranscriptAudio, type MarkerDisplay } from "./TranscriptAudio";
import {
  TranscriptContent,
  type ConversationAnchor,
} from "./TranscriptContent";
import { TranscriptNotes } from "./TranscriptNotes";
import { TranscriptTrace } from "./TranscriptTrace";
import { TranscriptVariables } from "./TranscriptVariables";
import type { ResolvedCellSupplierMap } from "./variables/ResolvedCellView";
import { ShowcaseReadOnlyNotice } from "./ShowcaseReadOnlyNotice";
import { CapacityWaitStatus } from "./CapacityWaitStatus";
import { ResultsRail } from "./transcripts/ResultsRail";
import { transcriptIdentityFacts } from "./transcripts/transcriptIdentity";
import {
  anchorUtteranceIndex,
  resultsRailRows,
} from "./transcripts/resultsRailRows";
import type { TranscriptLabeling } from "./transcripts/useLabelInspector";

type AnswerResponse = components["schemas"]["AnswerResponse"];
type NoteResponse = components["schemas"]["NoteResponse"];
type UserResponse = components["schemas"]["UserResponse"];
type TraceSpansResponse = components["schemas"]["TraceSpansResponse"];
type TranscriptResponse = components["schemas"]["TranscriptResponse"];
type TranscriptUtterance = components["schemas"]["TranscriptUtterance"];
type TranscriptVariablesResponse =
  components["schemas"]["TranscriptVariablesResponse"];

export interface TranscriptTimelineViewProps {
  viewerId?: string;
  activeSpanId?: string;
  agentVersionTag?: string | null;
  answers: ListQueryResult<AnswerResponse>;
  audioError?: string | null;
  audioIsPending?: boolean;
  audioPeaks?: number[] | null;
  audioSrc?: string | null;
  batchName?: string | null;
  // Playback position and state reported by the audio strip.
  currentMs?: number | null;
  isPlaying?: boolean;
  editingNoteBody: string;
  editingNoteId: string | null;
  evaluateAction?: ReactNode;
  // The results row that is expanded (`?label=`), resolved by the container.
  expandedId?: string;
  feed?: boolean;
  pinnedMarkerId?: string;
  isCreatingNote?: boolean;
  isDeletingNote?: boolean;
  isSavingNote?: boolean;
  labeling?: TranscriptLabeling;
  markerDisplayById?: Record<string, MarkerDisplay>;
  mutationError?: string | null;
  noteDraft: string;
  noteUsersById?: ReadonlyMap<string, UserResponse>;
  notes: ListQueryResult<NoteResponse>;
  onAddQuestion?: () => void;
  onBack: () => void;
  onCancelEditNote: () => void;
  onClosePanel: () => void;
  onCloseQuestion: () => void;
  onCreateNote: () => void;
  onDeleteNote: (note: NoteResponse) => void;
  onEditNoteBodyChange: (body: string) => void;
  onExpandAnswer: (answerId: string) => void;
  onNoteDraftChange: (body: string) => void;
  onOpenPanel: (panel: TranscriptTimelinePanel) => void;
  onPlayingChange?: (playing: boolean) => void;
  onReevaluate?: (markerId: string) => void;
  onSaveNote: (note: NoteResponse) => void;
  onSeekRequest: (startMs: number) => void;
  onStartEditNote: (note: NoteResponse) => void;
  onTimeUpdate?: (ms: number) => void;
  onTraceNodeRequest?: (nodeId: string) => void;
  panel?: TranscriptTimelinePanel;
  seekRequest?: AudioPlayerSeekRequest | null;
  traceError?: string | null;
  traces: TraceSpansResponse[];
  tracesIsPending?: boolean;
  transcript: TranscriptResponse | null;
  transcriptError?: string | null;
  transcriptIsPending?: boolean;
  variables?: TranscriptVariablesResponse | null;
  variablesError?: string | null;
  variablesIsPending?: boolean;
  variableSuppliers?: ResolvedCellSupplierMap;
}

const emptyMarkerDisplay: Record<string, MarkerDisplay> = {};

function firstSentence(text: string | null): string {
  const trimmed = text?.trim() ?? "";
  const match = /^[^.!?]*[.!?]/u.exec(trimmed);
  return (match?.[0] ?? trimmed).trim();
}
const noUtterances: TranscriptUtterance[] = [];

export function TranscriptTimelineView(props: TranscriptTimelineViewProps) {
  const { answers, expandedId, notes, panel, transcript } = props;
  const labeling = isShowcaseMode
    ? inactiveLabeling
    : (props.labeling ?? inactiveLabeling);
  const markerDisplayById = props.markerDisplayById ?? emptyMarkerDisplay;
  const utterances = transcript?.content ?? noUtterances;
  const rows = useMemo(
    () =>
      resultsRailRows(
        answers.items,
        markerDisplayById,
        utterances,
        props.pinnedMarkerId,
      ),
    [answers.items, markerDisplayById, utterances, props.pinnedMarkerId],
  );
  const anchors = useMemo(
    () =>
      rows.flatMap((row): ConversationAnchor[] => {
        const index = anchorUtteranceIndex(row.answer.locator);
        return index == null
          ? []
          : [
              {
                answerId: row.answer.id,
                index,
                name: row.name,
                summary: firstSentence(row.answer.justification),
                verdict: row.verdict,
              },
            ];
      }),
    [rows],
  );
  const hasVariables = transcript?.source === "simulated";
  const hasNotes = !isShowcaseMode;
  const panelOpen = transcript != null && panel != null;
  const feed = Boolean(props.feed) && !isShowcaseMode;
  const identity = transcript
    ? transcriptIdentityFacts(
        transcript,
        props.batchName,
        props.agentVersionTag,
      )
    : { facts: [], failure: [] };

  return (
    <Page variant="workspace">
      <PageHeader
        actions={
          <Inline gap={3}>
            {isShowcaseMode ? <ShowcaseReadOnlyNotice /> : props.evaluateAction}
            {labeling.feedBand}
          </Inline>
        }
        compact
        leading={
          <>
            <Button
              size="sm"
              type="button"
              variant="ghost"
              onClick={props.onBack}
            >
              Back
            </Button>
            {transcript ? (
              <>
                <Badge tone={transcriptStatusTone(transcript.status)}>
                  {transcript.status}
                </Badge>
                <CapacityWaitStatus
                  capacityWaitCause={transcript.capacity_wait_cause}
                  isWaitingForCapacity={transcript.is_waiting_for_capacity}
                />
                <Text
                  as="span"
                  className="marker-transcript-identity"
                  variant="muted"
                >
                  {identity.facts.join(" · ")}
                  {identity.failure.length > 0
                    ? ` · ${identity.failure.join(" · ")}`
                    : ""}
                </Text>
              </>
            ) : null}
          </>
        }
      />

      {props.transcriptIsPending ? (
        <Skeleton label="Loading transcript" shape="detail-field" />
      ) : null}
      {props.transcriptError ? (
        <InlineError>{props.transcriptError}</InlineError>
      ) : null}

      {transcript ? (
        <TranscriptAudio
          audioError={props.audioError}
          audioIsPending={props.audioIsPending}
          audioPeaks={props.audioPeaks}
          audioSrc={props.audioSrc}
          onPlayingChange={props.onPlayingChange}
          onTimeUpdate={props.onTimeUpdate}
          seekRequest={props.seekRequest}
          transcript={transcript}
        />
      ) : null}

      {transcript ? (
        <SplitPane
          ariaLabel="Transcript canvas"
          className="marker-transcript-canvas"
          first={
            <TranscriptContent
              anchors={anchors}
              currentMs={props.currentMs}
              isPlaying={props.isPlaying}
              locator={labeling.locator}
              scrollKey={expandedId}
              utterances={utterances}
              onAnchorSelect={props.onExpandAnswer}
              onSeek={props.audioSrc ? props.onSeekRequest : undefined}
              onTextPick={labeling.active ? labeling.onTextPick : undefined}
              onUtterancePick={
                labeling.active ? labeling.onUtterancePick : undefined
              }
            />
          }
          firstWidth="half"
          second={
            <ResultsRail
              editor={labeling.editor}
              errorMessage={answers.errorMessage}
              expandedId={expandedId}
              feed={feed}
              hasAudio={Boolean(props.audioSrc)}
              hasNotes={hasNotes}
              hasVariables={hasVariables}
              isPending={answers.isPending}
              readOnly={isShowcaseMode || labeling.hasMoreMarkers}
              rows={rows}
              usersById={props.noteUsersById}
              viewerId={props.viewerId}
              question={labeling.question}
              onAddQuestion={isShowcaseMode ? undefined : props.onAddQuestion}
              onBack={props.onBack}
              onCloseQuestion={props.onCloseQuestion}
              onExpand={props.onExpandAnswer}
              onOpenPanel={props.onOpenPanel}
              onOpenTraceSpan={props.onTraceNodeRequest}
              onReevaluate={props.onReevaluate}
              onSeek={props.onSeekRequest}
            />
          }
        />
      ) : null}

      <Drawer
        ariaLabel={panelLabel(panel)}
        isOpen={panelOpen}
        onDismiss={props.onClosePanel}
        size="lg"
      >
        {panel === "traces" ? (
          <TranscriptTrace
            activeSpanId={props.activeSpanId}
            error={props.traceError}
            isPending={props.tracesIsPending}
            onNodePick={labeling.active ? labeling.onTraceNodePick : undefined}
            selectedNodeId={
              labeling.locator?.kind === "trace_node"
                ? labeling.locator.node_id
                : undefined
            }
            traces={props.traces}
          />
        ) : null}
        {panel === "variables" && hasVariables ? (
          <TranscriptVariables
            errorMessage={props.variablesError}
            isPending={props.variablesIsPending}
            response={props.variables}
            suppliers={props.variableSuppliers}
          />
        ) : null}
        {panel === "notes" && hasNotes ? (
          <TranscriptNotes
            editingNoteBody={props.editingNoteBody}
            editingNoteId={props.editingNoteId}
            isCreatingNote={props.isCreatingNote}
            isDeletingNote={props.isDeletingNote}
            isSavingNote={props.isSavingNote}
            mutationError={props.mutationError}
            noteDraft={props.noteDraft}
            usersById={props.noteUsersById}
            notes={notes}
            onCancelEditNote={props.onCancelEditNote}
            onCreateNote={props.onCreateNote}
            onDeleteNote={props.onDeleteNote}
            onEditNoteBodyChange={props.onEditNoteBodyChange}
            onNoteDraftChange={props.onNoteDraftChange}
            onSaveNote={props.onSaveNote}
            onStartEditNote={props.onStartEditNote}
          />
        ) : null}
      </Drawer>
    </Page>
  );
}

function panelLabel(panel: TranscriptTimelinePanel | undefined): string {
  switch (panel) {
    case "notes":
      return "Notes";
    case "variables":
      return "Variables";
    default:
      return "Traces";
  }
}

const inactiveLabeling: TranscriptLabeling = {
  active: false,
  builtInMarkerIds: new Set(),
  editor: null,
  hasMoreMarkers: false,
  onTextPick: () => undefined,
  onTraceNodePick: () => undefined,
  onUtterancePick: () => undefined,
  question: null,
};
